import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react"
import { MEDIA_QUERIES } from "@/constants/ui"
import { useMarketingEntryCta } from "@/hooks/use-marketing-entry-cta"
import { cn } from "cn"
import { useInView } from "react-intersection-observer"

import { HERO_VIDEO_TYPE } from "@/lib/hero-video"
import { useTranslation } from "@/lib/i18n/i18n-context"
import { NO_TRANSLATE } from "@/lib/weglot"
import { Link } from "../ui/link"

const FEATURE_SCENE_IN_VIEW_OPTIONS = {
  // Wait until the callout area is well into the viewport, not just peeking in.
  threshold: 0.5,
  rootMargin: "-10% 0px",
}

type FeatureSceneContextValue = {
  revealed: boolean
  speaking: boolean
}

const FeatureSceneContext = createContext<FeatureSceneContextValue>({
  revealed: true,
  speaking: false,
})

function useFeatureScene() {
  return useContext(FeatureSceneContext)
}

function mergeRefs<T>(...refs: (Ref<T> | undefined)[]) {
  return (value: T | null) => {
    for (const ref of refs) {
      if (!ref) continue
      if (typeof ref === "function") {
        ref(value)
      } else {
        ref.current = value
      }
    }
  }
}

export function FeatureScene({
  id,
  className,
  style,
  sectionRef,
  children,
}: {
  id?: string
  className: string
  style?: CSSProperties
  sectionRef?: Ref<HTMLDivElement>
  children: ReactNode
}) {
  const [latched, setLatched] = useState(false)
  const prefersReducedMotion = useSyncExternalStore(
    () => () => {},
    () => window.matchMedia(MEDIA_QUERIES.reducedMotion).matches,
    () => false
  )
  const { ref: inViewRef, inView } = useInView({
    ...FEATURE_SCENE_IN_VIEW_OPTIONS,
    onChange: (visible) => {
      if (visible) {
        setLatched(true)
      }
    },
  })

  const revealed = latched || prefersReducedMotion
  const speaking = inView && !prefersReducedMotion

  return (
    <FeatureSceneContext.Provider value={{ revealed, speaking }}>
      <div
        id={id}
        ref={mergeRefs(inViewRef, sectionRef)}
        data-revealed={revealed || undefined}
        className={className}
        style={style}
      >
        {children}
      </div>
    </FeatureSceneContext.Provider>
  )
}

export function FeatureReveal({
  delay = 0,
  className,
  children,
}: {
  delay?: number
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn("feature-reveal", className)}
      style={{ "--reveal-delay": `${delay}ms` } as CSSProperties}
    >
      {children}
    </div>
  )
}

// A feature section's background video: the lower half on phones, fading in
// from the top, and the whole section from lg up.
export function FeatureSectionVideo({
  src,
  className,
}: {
  src: string
  className?: string
}) {
  return (
    <div
      className={cn(
        "absolute inset-x-0 bottom-0 h-[50%] overflow-hidden mask-[linear-gradient(to_top,black_68%,transparent)] lg:inset-0 lg:h-auto lg:mask-none",
        className
      )}
    >
      <LazyBackgroundVideo src={src} />
    </div>
  )
}

// `mobileSrc` plays where `mobileMedia` matches; `src` everywhere else.
export function LazyBackgroundVideo({
  src,
  mobileSrc,
  mobileMedia = MEDIA_QUERIES.xsMax,
  className,
}: {
  src: string
  mobileSrc?: string
  mobileMedia?: string
  className?: string
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const { ref: loadRef, inView: nearView } = useInView({
    triggerOnce: true,
    rootMargin: "50%",
  })
  const { ref: playRef, inView: canPlay } = useInView({
    threshold: 0.5,
  })

  const setRef = (element: HTMLVideoElement | null) => {
    videoRef.current = element
    loadRef(element)
    playRef(element)
  }

  useEffect(() => {
    if (!nearView) return
    const video = videoRef.current
    if (!video) return
    video.load()
  }, [nearView])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !nearView) return
    if (canPlay) {
      void video.play().catch(() => undefined)
    } else {
      video.pause()
    }
  }, [canPlay, nearView])

  return (
    <video
      ref={setRef}
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
      className={cn("absolute inset-0 size-full object-cover", className)}
    >
      {nearView && mobileSrc && (
        <source src={mobileSrc} type={HERO_VIDEO_TYPE} media={mobileMedia} />
      )}
      {nearView && <source src={src} type={HERO_VIDEO_TYPE} />}
    </video>
  )
}

function useVoiceAssistantSpeaking(
  svgRef: RefObject<SVGSVGElement | null>,
  speaking: boolean
) {
  useEffect(() => {
    const svg = svgRef.current
    if (!svg || !speaking) return
    if (window.matchMedia(MEDIA_QUERIES.reducedMotion).matches) return

    const bars = [...svg.querySelectorAll<SVGRectElement>("rect")]
    const rests = bars.map((bar) => ({
      height: bar.height.baseVal.value,
      y: bar.y.baseVal.value,
    }))

    const animations = bars.map((bar, i) => {
      const thickness = bar.width.baseVal.value
      const height = rests[i].height
      const y = rests[i].y
      const rest = { height: `${height}px`, y: `${y}px` }
      const minHeight = Math.max(thickness, height * (0.32 + (i % 4) * 0.1))
      const compressed = {
        height: `${minHeight}px`,
        y: `${y + (height - minHeight) / 2}px`,
      }

      return bar.animate(
        [
          { ...rest, easing: "ease-in-out" },
          { ...compressed, easing: "ease-in-out" },
          rest,
        ],
        {
          duration: 480 + (i % 3) * 140,
          delay: i * 75,
          iterations: Infinity,
        }
      )
    })

    return () => {
      animations.forEach((animation) => animation.cancel())
      bars.forEach((bar, i) => {
        bar.setAttribute("height", String(rests[i].height))
        bar.setAttribute("y", String(rests[i].y))
      })
    }
  }, [speaking, svgRef])
}

const VOICE_BARS = [
  { x: 26.4004, y: 43.3357, height: 12.4924 },
  { x: 33.0811, y: 37.2279, height: 24.7071 },
  { x: 39.7627, y: 31.6066, height: 35.9503 },
  { x: 46.4434, y: 40.5594, height: 18.0445 },
  { x: 53.124, y: 34.5215, height: 30.1205 },
  { x: 59.8047, y: 42.1558, height: 14.852 },
  { x: 66.4854, y: 43.3357, height: 12.4924 },
]

function VoiceBars() {
  return VOICE_BARS.map((bar) => (
    <rect
      key={bar.x}
      x={bar.x}
      y={bar.y}
      width="4.58054"
      height={bar.height}
      rx="2.29027"
      fill="white"
    />
  ))
}

export function VoiceAssistantMobileIcon() {
  const { speaking } = useFeatureScene()
  const svgRef = useRef<SVGSVGElement>(null)

  useVoiceAssistantSpeaking(svgRef, speaking)

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 100 100"
      fill="none"
      className="size-10 shrink-0"
      aria-hidden="true"
    >
      <circle cx="50" cy="50" r="50" fill="black" />
      <g transform="translate(1.27 0)">
        <VoiceBars />
      </g>
    </svg>
  )
}

export function VoiceAssistantOrb({
  initialProvider,
  className,
  onClick,
}: {
  initialProvider?: string
  className?: string
  onClick?: (action: "setup" | "get_started") => void
}) {
  const t = useTranslation()
  const { speaking } = useFeatureScene()
  const cta = useMarketingEntryCta({ initialProvider })
  const svgRef = useRef<SVGSVGElement>(null)
  const uid = useId().replaceAll(":", "")
  const radial0 = `${uid}-radial0`
  const linear1 = `${uid}-linear1`
  const radial2 = `${uid}-radial2`

  useVoiceAssistantSpeaking(svgRef, speaking)

  const orb = (
    <svg
      ref={svgRef}
      width="100"
      height="100"
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="49.5817"
        cy="49.5817"
        r="49.5817"
        fill={`url(#${radial0})`}
        fillOpacity="0.8"
      />
      <circle
        cx="49.5817"
        cy="49.5817"
        r="49.0817"
        stroke={`url(#${linear1})`}
        strokeOpacity="0.3"
      />
      <circle
        cx="49.5816"
        cy="49.5818"
        r="43.7887"
        fill="white"
        fillOpacity="0.2"
      />
      <circle
        cx="49.5816"
        cy="49.5818"
        r="43.7887"
        fill={`url(#${radial2})`}
        fillOpacity="0.6"
      />
      <VoiceBars />
      <defs>
        <radialGradient
          id={radial0}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(99.1634 99.1634 -99.1634 38.6419 0 0)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0.5" />
          <stop offset="1" stopColor="white" stopOpacity="0.2" />
        </radialGradient>
        <linearGradient
          id={linear1}
          x1="0"
          y1="0"
          x2="21.1412"
          y2="110.325"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#EEEDED" stopOpacity="0.5" />
        </linearGradient>
        <radialGradient
          id={radial2}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(87.5773 87.5773 -87.5773 34.127 5.79297 5.79309)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0.5" />
          <stop offset="1" stopColor="white" stopOpacity="0.2" />
        </radialGradient>
      </defs>
    </svg>
  )

  const interactiveClassName = cn(
    "block rounded-full transition-opacity hover:opacity-90 disabled:cursor-default",
    NO_TRANSLATE,
    className
  )

  if (cta.state === "get-started" && cta.href) {
    return (
      <Link
        href={cta.href}
        className={interactiveClassName}
        aria-label={t("marketingCtaGetStarted")}
        onClick={() => onClick?.("get_started")}
      >
        {orb}
      </Link>
    )
  }

  return (
    <button
      type="button"
      className={interactiveClassName}
      disabled={cta.state === "loading" || cta.setupDisabled}
      aria-busy={cta.state === "loading"}
      aria-label={t(cta.state === "loading" ? "loading" : "marketingCtaSetup")}
      onClick={() => {
        onClick?.("setup")
        cta.onSetup()
      }}
    >
      {orb}
    </button>
  )
}
